import React from 'react';

import isEmpty from 'lodash/isEmpty';

import { useDataAttributes } from '@lumapps/data-attributes';
import { Pagination, PaginationMode } from '@lumapps/lumx-layouts/components/Pagination';
import { mdiSparkles, mdiCheckboxMarkedCircleOutline, mdiSubdirectoryArrowLeft } from '@lumapps/lumx/icons';
import {
    Emphasis,
    ColorPalette,
    Button,
    FlexBox,
    Icon,
    Orientation,
    Placement,
    PopoverDialog,
    ProgressCircular,
    Size,
    Typography,
    Text,
    TextField,
} from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { useIsInView } from '@lumapps/utils/hooks/useIsInView';
import { BaseSelection } from '@lumapps/wrex-core/slate';

import { UseRefineContentOutput } from '../../hooks/useRefineContentDialog';
import { WREX_GENERATED_CONTENT } from '../../keys';
import { PopoverStyle } from '../../types';

import '../index.scss';

const CLASSNAME = 'generative-content';

export interface UpdateContentDialogProps {
    anchorRef: React.MutableRefObject<null>;
    hasCurrentRange: boolean;
    isRefiningContent: UseRefineContentOutput['isRefiningContent'];
    newSuggestions?: UseRefineContentOutput['newSuggestions'];
    popoverStyle?: PopoverStyle;
    slateSelection: BaseSelection;
    onCancelRefinement: UseRefineContentOutput['onCancelRefinement'];
    onInsertNewContent: UseRefineContentOutput['onInsertNewContent'];
    onRefineContent: UseRefineContentOutput['onRefineContent'];
    onClose: () => void;
    setNewSuggestions: UseRefineContentOutput['setNewSuggestions'];
}

export const UpdateContentDialog = ({
    anchorRef,
    hasCurrentRange,
    isRefiningContent,
    newSuggestions,
    popoverStyle,
    slateSelection,
    onCancelRefinement,
    onInsertNewContent,
    onRefineContent,
    onClose,
    setNewSuggestions,
}: UpdateContentDialogProps) => {
    const { translateKey } = useTranslate();
    const { get } = useDataAttributes('poc-ai-adapt-selection-ask-ai');

    const { inView, ref: inViewRef } = useIsInView({
        threshold: 1,
        trackVisibility: true,
        delay: 100,
        initialInView: true,
    });

    const dialogRef = React.useRef<HTMLDivElement | null>(null);

    const [detailedPrompt, setDetailedPrompt] = React.useState('');
    const [index, setIndex] = React.useState(newSuggestions ? newSuggestions.length : 0);

    // Ask AI again using the text input
    const onDetailPrompt = () => {
        if (newSuggestions && !isEmpty(newSuggestions)) {
            setNewSuggestions(newSuggestions);
            onRefineContent(detailedPrompt, newSuggestions[index], undefined, true);
            setDetailedPrompt('');
        }
    };

    // Press Enter to submit a new prompt
    const onKeyDown = (ev: KeyboardEvent) => {
        if (ev.key === 'Enter') {
            onDetailPrompt();
        }
    };

    // Handle suggestions pagination
    const onPreviousClick = () => {
        setIndex((i) => i - 1);
    };
    const onNextClick = () => {
        setIndex((i) => i + 1);
    };

    // When suggestions are updated, display the last one
    React.useEffect(() => {
        if (newSuggestions) {
            setIndex(newSuggestions.length >= 1 ? newSuggestions.length - 1 : 0);
        }
    }, [newSuggestions]);

    // If popover isn't into view, we scroll to its position
    const { current } = dialogRef;
    React.useEffect(() => inViewRef(current), [current, inViewRef]);

    React.useEffect(() => {
        if (isRefiningContent && !inView && current) {
            current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [isRefiningContent, newSuggestions, inView, current]);

    return (
        <PopoverDialog
            style={popoverStyle}
            anchorRef={anchorRef}
            isOpen={hasCurrentRange}
            placement={Placement.BOTTOM_START}
            className={`${CLASSNAME}__popover`}
            fitWithinViewportHeight
            closeOnClickAway
            onClose={onClose}
            aria-label={translateKey(GLOBAL.AI_GENERATED_RESULT)}
            ref={dialogRef}
            // We set usePortal to false to have an inline dialog
            usePortal={false}
        >
            <FlexBox orientation={Orientation.vertical}>
                {isRefiningContent && (
                    <>
                        <Text as="p" color={ColorPalette.blue} typography={Typography.overline}>
                            <Icon icon={mdiSparkles} />
                            {translateKey(WREX_GENERATED_CONTENT.GENERATING_RESULT)}
                        </Text>
                        <ProgressCircular className={`${CLASSNAME}__loading`} />
                    </>
                )}

                {!isRefiningContent && newSuggestions && (
                    <>
                        <FlexBox orientation={Orientation.horizontal} hAlign="center" vAlign="space-between">
                            <Text as="p" color={ColorPalette.blue} typography={Typography.overline}>
                                <Icon icon={mdiSparkles} />
                                {translateKey(GLOBAL.AI_GENERATED_RESULT)}
                            </Text>

                            <Pagination
                                nbOfPages={newSuggestions.length}
                                currentPage={index}
                                onNextClick={onNextClick}
                                onPreviousClick={onPreviousClick}
                                isFullWidth={false}
                                mode={PaginationMode.onMode}
                            />
                        </FlexBox>

                        <FlexBox orientation={Orientation.horizontal} className={`${CLASSNAME}__generated-content`}>
                            <Text
                                typography={Typography.body1}
                                as="p"
                                className={`${CLASSNAME}__generated-content-text`}
                            >
                                {newSuggestions[index]}
                            </Text>
                            <FlexBox orientation={Orientation.horizontal} gap={Size.regular} noShrink>
                                <Button
                                    onClick={onCancelRefinement}
                                    emphasis={Emphasis.medium}
                                    {...get({ element: 'ignore' })}
                                >
                                    {translateKey(WREX_GENERATED_CONTENT.IGNORE_SUGGESTION)}
                                </Button>
                                <Button
                                    onClick={() => onInsertNewContent(slateSelection, index)}
                                    leftIcon={mdiCheckboxMarkedCircleOutline}
                                    {...get({ element: 'replace' })}
                                >
                                    {translateKey(WREX_GENERATED_CONTENT.REPLACE_SELECTION)}
                                </Button>
                            </FlexBox>
                        </FlexBox>
                    </>
                )}

                {!isEmpty(newSuggestions) && (
                    <TextField
                        icon={mdiSparkles}
                        placeholder={translateKey(WREX_GENERATED_CONTENT.GIVE_A_TASK)}
                        className={`${CLASSNAME}__detailed-prompt`}
                        onChange={(prompt) => setDetailedPrompt(prompt)}
                        afterElement={
                            !isEmpty(detailedPrompt) && (
                                <Icon hasShape icon={mdiSubdirectoryArrowLeft} size={Size.s} onClick={onDetailPrompt} />
                            )
                        }
                        isDisabled={isRefiningContent}
                        value={detailedPrompt}
                        onKeyDown={onKeyDown}
                    />
                )}
            </FlexBox>
        </PopoverDialog>
    );
};
